import { Box, useTheme, useMediaQuery } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import IFundTypeHistoryPeriod from '../../../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';

import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from '../../../../../../app/features/settings/providers/AppSettingsProvider';
import investmentTypeHistoryLang from '../../langs/investmentTypeHistoryLang';
import AppLoaderGraph from '../../../../../../app/features/loader/graph/AppLoaderGraph';
import _ from 'lodash';
import langInvestWordLang from '../../../../langs/langInvestWordLang';
import useAppFormatAmount from '../../../../../../app/features/format/hooks/useAppFormatAmount';
import CAppPaper from '../../../../../../app/components/CAppPaper';
import investmentFundLang from '../../../../langs/investmentFundLang';
import styleVariables from '../../../../../../app/styles/styleVariables';
import useAppFormatNumber from '../../../../../../app/features/format/hooks/useAppFormatNumber';
import { TInvestFundCurrency } from '../../../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../../../../components/CGraphMultiCurrency';

type CHistoryTotalValueProps = {
	historyPeriods: IFundTypeHistoryPeriod[] | null | undefined;
	btnPrevious: ReactNode;
	btnNext: ReactNode;
	allHistoryPeriods: IFundTypeHistoryPeriod[] | null | undefined;
};
const CHistoryTotalValue = (props: CHistoryTotalValueProps) => {
	const { historyPeriods, btnPrevious, btnNext, allHistoryPeriods } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [data, setData] = useState<Record<string, unknown>[] | null>(null);

	const [currency, setCurrency] = useState<TInvestFundCurrency | null>(null);
	useEffect(() => {
		if (historyPeriods) {
			const currencies = _.uniq(historyPeriods.map((historyPeriod) => historyPeriod.stat.currency));
			setCurrency(currencies && currencies.length === 1 ? currencies[0] : null);
		}
	}, [historyPeriods]);

	useEffect(() => {
		if (historyPeriods && allHistoryPeriods) {
			// On filtre sur les cumulatedNetCalledCapital existants pour être sûr de ressortir un objet avec maxBy
			const allHistoryPeriodsTmp = allHistoryPeriods.filter((x) => x.stat.cumulatedNetCalledCapital);
			const maxCumulatedNetCalledCapital =
				_.maxBy(allHistoryPeriodsTmp, 'stat.cumulatedNetCalledCapital')?.stat.cumulatedNetCalledCapital ?? null;

			// Init des data
			setData(
				historyPeriods.map((x, index) => {
					// Cumulated Net Call Capital
					const hideCumulatedNetCalledCapitalDisplay =
						(historyPeriods[index + 1] && historyPeriods[index + 1].stat.totalValue === null) ||
						(historyPeriods[index] && historyPeriods[index].stat.totalValue === null)
							? true
							: false;
					const hideCumulatedNetCalledCapitalPoint =
						historyPeriods[index] && historyPeriods[index].stat.totalValue === null ? true : false;

					// Total Value
					const hideTotalValueDisplay =
						(historyPeriods[index + 1] && historyPeriods[index + 1].stat.totalValue === null) ||
						(historyPeriods[index] && historyPeriods[index].stat.totalValue === null)
							? true
							: false;
					const hideTotalValuePoint =
						historyPeriods[index] && historyPeriods[index].stat.totalValue === null ? true : false;
					// Total capital calls
					const hideTotalCapitalCallsDisplay =
						(historyPeriods[index + 1] && historyPeriods[index + 1].stat.totalCapitalCalls === null) ||
						(historyPeriods[index] && historyPeriods[index].stat.totalCapitalCalls === null)
							? true
							: false;
					const hideTotalCapitalCallsPoint =
						historyPeriods[index] && historyPeriods[index].stat.totalCapitalCalls === null ? true : false;
					return {
						quarter: x.name,
						// Cumulated Net Call Capital
						cumulatedNetCalledCapital: displayRounding(x.stat.cumulatedNetCalledCapital ?? 0, rounding),
						cumulatedNetCalledCapitalText: x.stat.cumulatedNetCalledCapital
							? formatAmount(displayRounding(x.stat.cumulatedNetCalledCapital ?? 0, rounding)) +
							  ' ' +
							  displayRoundingSuffix(rounding) +
							  displayCurrencySymbol(currency)
							: t(langInvestWordLang.nc.tag),
						cumulatedNetCalledCapitalsStrokeSettings: {
							strokeOpacity: hideCumulatedNetCalledCapitalDisplay ? 0 : 1,
						},
						cumulatedNetCalledCapitalsFillSettings: {
							fillOpacity: hideCumulatedNetCalledCapitalDisplay ? 0 : 1,
						},
						cumulatedNetCalledCapitalsBulletSettings: {
							opacity: hideCumulatedNetCalledCapitalPoint ? 0 : 1,
							fillOpacity: hideCumulatedNetCalledCapitalPoint ? 0 : 1,
							strokeOpacity: hideCumulatedNetCalledCapitalPoint ? 0 : 1,
							radius: hideCumulatedNetCalledCapitalPoint ? 0 : 2,
							strokeWidth: hideCumulatedNetCalledCapitalPoint ? 0 : 0,
						},
						// MAX Cumulated Net Call Capital
						maxCumulatedNetCalledCapital: displayRounding(maxCumulatedNetCalledCapital ?? 0, rounding),
						maxCumulatedNetCalledCapitalText: maxCumulatedNetCalledCapital
							? formatAmount(displayRounding(maxCumulatedNetCalledCapital ?? 0, rounding)) +
							  ' ' +
							  displayRoundingSuffix(rounding) +
							  displayCurrencySymbol(currency)
							: t(langInvestWordLang.nc.tag),
						// Total Value
						totalValue: displayRounding(x.stat.totalValue ?? 0, rounding),
						totalValueText: x.stat.totalValue
							? formatAmount(displayRounding(x.stat.totalValue ?? 0, rounding)) +
							  ' ' +
							  displayRoundingSuffix(rounding) +
							  displayCurrencySymbol(currency)
							: t(langInvestWordLang.nc.tag),
						totalValueStrokeSettings: {
							strokeOpacity: hideTotalValueDisplay ? 0 : 1,
						},
						totalValueFillSettings: {
							fillOpacity: hideTotalValueDisplay ? 0 : 1,
						},
						totalValueBulletSettings: {
							opacity: hideTotalValuePoint ? 0 : 1,
							fillOpacity: hideTotalValuePoint ? 0 : 1,
							strokeOpacity: hideTotalValuePoint ? 0 : 1,
							radius: hideTotalValuePoint ? 0 : 2,
							strokeWidth: hideTotalValuePoint ? 0 : 0,
						},
						// Total capital calls
						totalCapitalCalls: displayRounding(x.stat.totalCapitalCalls ?? 0, rounding),
						totalCapitalCallsText: x.stat.totalCapitalCalls
							? formatAmount(displayRounding(x.stat.totalCapitalCalls ?? 0, rounding)) +
							  ' ' +
							  displayRoundingSuffix(rounding) +
							  displayCurrencySymbol(currency)
							: t(langInvestWordLang.nc.tag),
						totalCapitalCallsStrokeSettings: {
							strokeOpacity: hideTotalCapitalCallsDisplay ? 0 : 1,
						},
						totalCapitalCallsFillSettings: {
							fillOpacity: hideTotalCapitalCallsDisplay ? 0 : 1,
						},
						totalCapitalCallsBulletSettings: {
							opacity: hideTotalCapitalCallsPoint ? 0 : 1,
							fillOpacity: hideTotalCapitalCallsPoint ? 0 : 1,
							strokeOpacity: hideTotalCapitalCallsPoint ? 0 : 1,
							radius: hideTotalCapitalCallsPoint ? 0 : 2,
							strokeWidth: hideTotalCapitalCallsPoint ? 0 : 0,
						},
					};
				}),
			);
		}
	}, [
		allHistoryPeriods,
		currency,
		displayCurrencySymbol,
		displayRounding,
		displayRoundingSuffix,
		formatAmount,
		historyPeriods,
		rounding,
		t,
	]);

	const [yMax, setYMax] = useState<number | null>(null);
	useEffect(() => {
		if (allHistoryPeriods) {
			setYMax(
				_.max(
					allHistoryPeriods.map((x) =>
						displayRounding(
							_.max([x.stat.totalValue, x.stat.totalCapitalCalls, x.stat.cumulatedNetCalledCapital]) ?? 0,
							rounding,
						),
					),
				) ?? null,
			);
		}
	}, [allHistoryPeriods, displayRounding, rounding]);

	useEffect(() => {
		if (data && yMax && currency) {
			const root = am5.Root.new('total-value-graph');

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					layout: root.verticalLayout,
				}),
			);

			const lineXAxisColor = am5.Theme.new(root);
			lineXAxisColor.rule('Grid', ['base']).setAll({
				strokeOpacity: 1,
				stroke: am5.color('#1a2b48'),
				strokeWidth: 2,
			});

			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root), lineXAxisColor]);
			} else {
				root.setThemes([am5themes_Responsive.new(root), lineXAxisColor]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
			cursor.lineY.set('visible', false);
			cursor.lineX.set('visible', false);

			const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
			xRenderer.labels.template.setAll({
				centerY: am5.p50,
				paddingTop: 10,
				paddingBottom: 20,
				paddingRight: 15,
				textAlign: 'center',
				fontSize: '13px',
				fontFamily: "'Poppins', sans-serif",
				fontWeight: '500',
				fill: am5.color('#1A2B48'),
			});

			xRenderer.grid.template.setAll({
				visible: false,
			});

			const xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					maxDeviation: 0.3,
					categoryField: 'quarter',
					opacity: 1,
					renderer: xRenderer,
				}),
			);

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					min: 0,
					max: yMax + (yMax * 20) / 100,
					maxDeviation: 0.3,
					renderer: am5xy.AxisRendererY.new(root, {}),
				}),
			);

			const yRenderer = yAxis.get('renderer');
			yRenderer.labels.template.setAll({
				fill: am5.color(darkModeEnabled ? '#ccc' : '#AAA'),
				fontSize: '10px',
			});

			const tooltip = am5.Tooltip.new(root, {
				// Disable amCharts' default background and text color so we can fully
				// control the look via the explicit settings below.
				getFillFromSprite: false,
				autoTextColor: false,

				pointerOrientation: 'left',
				dx: 10,
				labelText: `[bold]{quarter}[/]\n\n[width:240px]${t(
					investmentTypeHistoryLang.graph_legend_total_value.tag,
				)}[/][#2B70E7][bold]{totalValueText}[/][/][/]\n\n[width:240px]${t(
					investmentTypeHistoryLang.graph_legend_total_capital_calls.tag,
				)}[/][#86D9D8][bold]{totalCapitalCallsText}[/][/][/]\n\n[width:240px]${t(
					investmentTypeHistoryLang.graph_legend_cumulated_net_called_capital.tag,
				)}[/][${darkModeEnabled ? '#1A2B48' : '#FFF'}][bold]{cumulatedNetCalledCapitalText}[/][/]`,
			});

			tooltip?.get?.('background')?.setAll({
				fill: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				stroke: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				fillOpacity: darkModeEnabled ? 0.95 : 0.8,
			});

			const tooltipTransparent = am5.Tooltip.new(root, {
				getFillFromSprite: false,
				autoTextColor: false,
			});
			tooltipTransparent?.get?.('background')?.setAll({
				opacity: 0,
			});

			// Total Value
			const series = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_total_value.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					valueXField: 'quarter',
					valueYField: 'totalValue',
					fill: am5.color(0x2b70e7),
					stroke: am5.color(0x2b70e7),
					seriesTooltipTarget: 'bullet',
					sequencedInterpolation: true,
					categoryXField: 'quarter',
					tooltip: tooltip,
				}),
			);

			series.strokes.template.setAll({
				strokeWidth: 2,
				templateField: 'totalValueStrokeSettings',
			});

			series.fills.template.setAll({
				templateField: 'totalValueFillSettings',
			});

			series.bullets.push(function () {
				// create the circle first
				const circle = am5.Circle.new(root, {
					stroke: am5.color('#2b70e7'),
					interactive: true,
					fill: am5.color(0xffffff),
					templateField: 'totalValueBulletSettings',
				});

				circle.states.create('hover', {
					opacity: 1,
					radius: 6,
				});

				return am5.Bullet.new(root, {
					sprite: circle,
				});
			});

			// Total capital calls
			const series2 = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_total_capital_calls.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					valueXField: 'quarter',
					valueYField: 'totalCapitalCalls',
					fill: am5.color(0x86d9d8),
					stroke: am5.color(0x86d9d8),
					seriesTooltipTarget: 'bullet',
					sequencedInterpolation: true,
					categoryXField: 'quarter',
					tooltip: tooltipTransparent,
				}),
			);

			series2.strokes.template.setAll({
				strokeWidth: 2,
				templateField: 'totalCapitalCallsStrokeSettings',
			});

			series2.fills.template.setAll({
				templateField: 'totalCapitalCallsFillSettings',
			});

			series2.bullets.push(function () {
				const circle = am5.Circle.new(root, {
					stroke: am5.color('#86D9D8'),
					interactive: true,
					fill: am5.color(0xffffff),
					templateField: 'totalCapitalCallsBulletSettings',
				});

				circle.states.create('hover', {
					opacity: 1,
					radius: 6,
				});

				return am5.Bullet.new(root, {
					sprite: circle,
				});
			});

			// Capital net
			const series3 = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_cumulated_net_called_capital.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					valueXField: 'quarter',
					valueYField: 'cumulatedNetCalledCapital',
					fill: am5.color(darkModeEnabled ? 0xffffff : 0x1a2b48),
					stroke: am5.color(darkModeEnabled ? 0xffffff : 0x1a2b48),
					seriesTooltipTarget: 'bullet',
					sequencedInterpolation: true,
					categoryXField: 'quarter',
					tooltip: tooltipTransparent,
				}),
			);

			series3.strokes.template.setAll({
				strokeWidth: 2,
				templateField: 'cumulatedNetCalledCapitalsStrokeSettings',
			});

			series3.fills.template.setAll({
				templateField: 'cumulatedNetCalledCapitalsFillSettings',
			});

			series3.bullets.push(function () {
				const circle = am5.Circle.new(root, {
					stroke: am5.color(darkModeEnabled ? 0xffffff : 0x1a2b48),
					interactive: true,
					fill: am5.color(0xffffff),
					templateField: 'cumulatedNetCalledCapitalsBulletSettings',
				});

				circle.states.create('hover', {
					opacity: 1,
					radius: 6,
				});

				return am5.Bullet.new(root, {
					sprite: circle,
				});
			});

			// MAX Cumulated Net Called Capital
			const series4 = chart.series.push(
				am5xy.LineSeries.new(root, {
					name:
						t(investmentTypeHistoryLang.graph_legend_max_cumulated_net_called_capital.tag) +
						' : ' +
						data[0].maxCumulatedNetCalledCapitalText,

					xAxis: xAxis,
					yAxis: yAxis,
					valueYField: 'maxCumulatedNetCalledCapital',
					fill: am5.color(styleVariables.greyColor),
					stroke: am5.color(darkModeEnabled ? 0xffffff : 0x1a2b48),
					sequencedInterpolation: true,
					categoryXField: 'quarter',
				}),
			);

			series4.strokes.template.setAll({
				strokeWidth: 0.1,
				strokeDasharray: [6, 6],
			});

			series4.fills.template.setAll({
				fillOpacity: 0.2,
				visible: true,
			});

			xAxis.data.setAll(data);
			series.data.setAll(data);
			series2.data.setAll(data);
			series3.data.setAll(data);
			series4.data.setAll(data);

			series.appear(1000);
			series2.appear(1000);
			series3.appear(1000);
			series4.appear(0);
			chart.appear(1000, 100);

			cursor.setAll({
				xAxis: xAxis,
				yAxis: yAxis,
			});

			let previousBulletSprites: am5.Sprite[] = [];
			const cursorMoved = () => {
				for (let i = 0; i < previousBulletSprites.length; i++) {
					previousBulletSprites[i].unhover();
				}
				previousBulletSprites = [];
				chart.series.each(function (serie) {
					if (serie.get('tooltip')) {
						const dataItem = serie?.get?.('tooltip')?.dataItem;
						if (dataItem && dataItem.bullets) {
							const bulletSprite = dataItem.bullets[0].get('sprite');
							bulletSprite.hover();
							previousBulletSprites.push(bulletSprite);
						}
					}
				});
			};
			cursor.events.on('cursormoved', cursorMoved);

			const cursorStroke = chart.get('cursor');
			cursorStroke?.lineY.setAll({
				visible: false,
			});
			cursorStroke?.lineX.setAll({
				visible: false,
			});

			let legend: am5.Legend;
			let legendRoot: am5.Root;

			if (isMobile) {
				legendRoot = am5.Root.new('total-value-graph-legend');
				legend = legendRoot.container.children.push(
					am5.Legend.new(legendRoot, {
						centerX: am5.percent(50),
						x: am5.percent(50),
						layout: legendRoot.verticalLayout,
						useDefaultMarker: true,
						height: am5.percent(100),
						verticalScrollbar: am5.Scrollbar.new(legendRoot, {
							orientation: 'vertical',
						}),
					}),
				);

				legend.markerRectangles.template.setAll({
					cornerRadiusTL: 10,
					cornerRadiusTR: 10,
					cornerRadiusBL: 10,
					cornerRadiusBR: 10,
				});

				legend.labels.template.setAll({
					fontSize: 14,
					fontWeight: '300',
					fill: am5.color(theme.palette.primary.main),
				});
				legend.valueLabels.template.setAll({
					fontSize: 14,
					fontWeight: '400',
					fill: am5.color(theme.palette.primary.main),
				});
			} else {
				legend = chart.children.push(
					am5.Legend.new(root, {
						centerX: am5.p50,
						x: am5.p50,
						y: am5.percent(90),
					}),
				);
			}

			legend.data.setAll(chart.series.values);

			return () => {
				root.dispose();
				isMobile ? legendRoot.dispose() : null;
			};
		}
	}, [data, t, darkModeEnabled, rounding, yMax, isMobile, i18n.language, theme.palette.primary.main, currency]);

	return (
		<>
			{data !== null ? (
				<>
					{data.length === 0 ? (
						<>
							<CAppPaper
								variant="outlined"
								sx={{
									textAlign: 'center',
									padding: '50px',
									width: '100%',
								}}
							>
								{t(investmentFundLang.text_no_data_available_yet.tag)}
							</CAppPaper>
						</>
					) : (
						<>
							{currency ? (
								<>
									<Box sx={{ position: 'relative', height: '300px' }}>
										<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', left: '0px' }}>
											{btnPrevious}
										</Box>
										<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', right: '0px' }}>
											{btnNext}
										</Box>
										<Box
											id="total-value-graph"
											sx={{ height: '300px', width: '100%', marginLeft: '-10px' }}
										/>
									</Box>
									<Box
										id="total-value-graph-legend"
										sx={{
											height: '100px',
											display: { xs: 'block', sm: 'none' },
											marginBottom: '20px',
										}}
									/>
								</>
							) : (
								<CGraphMultiCurrency variant="rectangular" height={300} />
							)}
						</>
					)}
				</>
			) : (
				<AppLoaderGraph height={280} />
			)}
		</>
	);
};

export default CHistoryTotalValue;
